import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, EffectCoverflow, Keyboard, Navigation, Pagination } from "swiper/modules";
import { RankedPlayer } from "@uno/protocol";
import PlayerScoreCard from "./PlayerScoreCard";

type Props = {
    players: RankedPlayer[];
    myPlayerId: string | undefined;
    isMobile: boolean;
    isCompact: boolean;
    isDense: boolean;
};

const RankingView: React.FC<Props> = ({
    players,
    myPlayerId,
    isMobile,
    isCompact,
    isDense,
}) => {
    const myIndex = Math.max(0, players.findIndex((p) => p.id === myPlayerId));

    if(isMobile) {
        return (
            <>
                <Swiper
                    modules={[Pagination, Keyboard, A11y, EffectCoverflow]}
                    effect="coverflow"
                    coverflowEffect={{ rotate: 35, stretch: -10, depth: 140, modifier: 1, slideShadows: false }}
                    slidesPerView="auto"
                    spaceBetween={0}
                    centeredSlides={true}
                    pagination={{ clickable: true, el: ".ge-pagination" }}
                    keyboard={{ enabled: true }}
                    initialSlide={myIndex}
                    className={`ge-swiper ${
                        isDense
                            ? "ge-swiper-dense"
                            : isCompact
                                ? "ge-swiper-compact"
                                : ""
                    }`}
                >
                    {players.map((player, index) => (
                        <SwiperSlide key={player.id}>
                            <PlayerScoreCard
                                player={player}
                                rank={index + 1}
                                isMe={player.id === myPlayerId}
                                isCompact={isCompact}
                                isDense={isDense}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
                <div className={`ge-pagination justify-center gap-1.5 ${
                    isDense ? "hidden" : `flex ${isCompact ? "mt-1" : "mt-3"}`
                }`}></div>
            </>
        );
    }

    return (
        <div className={`relative w-full min-w-0 mx-auto ${isCompact ? "max-w-[860px] px-10" : "max-w-[960px] px-12"}`}>
            <Swiper
                modules={[Navigation, Keyboard, A11y]}
                slidesPerView={4}
                slidesPerGroup={4}
                spaceBetween={isCompact ? 8 : 12}
                centerInsufficientSlides={true}
                navigation={{ prevEl: ".ge-prev", nextEl: ".ge-next" }}
                keyboard={{ enabled: true }}
                initialSlide={Math.floor(myIndex / 4) * 4}
                className={`ge-swiper-desktop ${isCompact ? "ge-swiper-desktop-compact" : ""}`}
            >
                {players.map((player, index) => (
                    <SwiperSlide key={player.id}>
                        <PlayerScoreCard
                            player={player}
                            rank={index + 1}
                            isMe={player.id === myPlayerId}
                            isCompact={isCompact}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
            <button
                className={`ge-prev absolute left-0 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed z-10 ${
                    isCompact ? "w-8 h-8" : "w-10 h-10"
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
            </button>
            <button
                className={`ge-next absolute right-0 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed z-10 ${
                    isCompact ? "w-8 h-8" : "w-10 h-10"
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </button>
        </div>
    );
};

export default RankingView;
